import { useState, type KeyboardEvent } from 'react'
import { ApiError } from '../../api/client'
import { useAction } from '../../api/queries'
import { Field } from '../../components/ui'

interface Props {
  /** Accessible group name, e.g. "Nueva planta". */
  title: string
  endpoint: string
  /** Query prefixes to refresh after a successful create. */
  invalidate: string[]
  /** Extra payload fields (e.g. the parent plant id for an area). */
  extra?: Record<string, unknown>
  labels: { code: string; name: string }
  idPrefix: string
  onCreated: (id: number) => void
  onCancel: () => void
}

/**
 * Compact create form for catalogue rows (plant/area). It is deliberately NOT a <form>: it lives inside the equipment
 * form, so nesting would be invalid HTML and Enter would submit the outer form. Enter is handled here instead.
 */
export function InlineCreateForm({ title, endpoint, invalidate, extra, labels, idPrefix, onCreated, onCancel }: Props) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [local, setLocal] = useState<Record<string, string>>({})
  const [error, setError] = useState<ApiError | null>(null)
  const create = useAction<Record<string, unknown>, { id: number }>('post', endpoint, invalidate)

  async function submit() {
    if (create.isPending) return
    setError(null)
    const problems: Record<string, string> = {}
    if (!code.trim()) problems.code = 'El código es obligatorio.'
    if (!name.trim()) problems.name = 'El nombre es obligatorio.'
    setLocal(problems)
    if (Object.keys(problems).length) return
    try {
      const created = await create.mutateAsync({ ...extra, code: code.trim(), name: name.trim() })
      onCreated(created.id)
    } catch (err) { setError(err as ApiError) }
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); void submit() }
    if (e.key === 'Escape') { e.stopPropagation(); onCancel() } // do not close the whole modal
  }

  const apiFields = error?.fieldErrors ?? {}
  const fe = { ...apiFields, ...local }
  const other = Object.entries(apiFields).filter(([k]) => k !== 'code' && k !== 'name').map(([, m]) => m)
  const generic = error ? (other.length ? other.join(' ') : Object.keys(apiFields).length ? null : error.message) : null

  return (
    <section role="group" aria-label={title} className="form-stack" onKeyDown={onKeyDown}>
      <Field label={labels.code} htmlFor={`${idPrefix}-code`} error={fe.code}><input id={`${idPrefix}-code`} value={code} onChange={(e) => setCode(e.target.value)} maxLength={20} autoComplete="off" autoFocus /></Field>
      <Field label={labels.name} htmlFor={`${idPrefix}-name`} error={fe.name}><input id={`${idPrefix}-name`} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="off" /></Field>
      {generic ? <p role="alert" className="form-error">{generic}</p> : null}
      <div className="row">
        <button type="button" className="btn btn--ghost" onClick={onCancel}>Cancelar</button>
        <button type="button" className="btn btn--primary" onClick={() => void submit()} disabled={create.isPending}>Crear</button>
      </div>
    </section>
  )
}
